import { Join } from '@amplify/free-to-paid/dist/features/Join';
import clsx from 'clsx';

import {
  JOIN_CODE_NOTIFICATION_MESSAGE,
  NOTIFICATION_SHOW_DELAY,
} from 'src/constants/notifications.constants';
import { useCommonXapiContextPayloadHook } from 'src/hooks/use-common-xapi-context-payload.hook';
import { useIsMiddleSchoolView } from 'src/hooks/use-is-middle-school-view';
import { XApiEventKeys } from 'src/interfaces/event.types';
import { getAmpConfig } from 'src/services/amp-config.service';
import AuthService from 'src/services/api/auth.service';
import eventService from 'src/services/event.service';
import { notificationsService } from 'src/services/notifications.service';
import { useAssignmentsRefetchWithCurrentArgs } from 'src/store/api/assignment';
import { useUserClassesRefetchWithCurrentArgs } from 'src/store/api/onboarding';
import { useUserAssetsRefetchWithCurrentArgs } from 'src/store/api/retail-composite';
import {
  setIsSingleSessionCodeModalOpen,
  useIsSingleSessionCodeModalOpen,
} from 'src/store/features';
import { useAppDispatch } from 'src/store/redux';

import { CodeTypes } from './constants';

import './join-code-modal.scss';

interface JoinCodeInfo {
  code: string;
  source: CodeTypes | null;
  shouldJoinClass: boolean;
}

export const JoinCodeModal = () => {
  const keycloak = AuthService.getKC();

  const dispatch = useAppDispatch();
  const isSingleSessionCodeModalOpen = useIsSingleSessionCodeModalOpen();
  const isMiddleSchoolView = useIsMiddleSchoolView();
  const commonXapiContextPayload = useCommonXapiContextPayloadHook();

  const refetchAssignmentsWithCurrentArgs =
    useAssignmentsRefetchWithCurrentArgs();
  const refetchUserClassesWithCurrentArgs =
    useUserClassesRefetchWithCurrentArgs();
  const refetchUserAssetsWithCurrentArgs =
    useUserAssetsRefetchWithCurrentArgs();

  const handleJoinComponentClose = () => {
    dispatch(setIsSingleSessionCodeModalOpen(false));
  };

  const sendEnterCodeXAPIEvent = ({
    isSuccess,
    source,
    shouldJoinClass,
    classCode,
  }: {
    isSuccess: boolean;
    source?: CodeTypes | null;
    shouldJoinClass: boolean;
    classCode?: string;
  }) => {
    eventService.send(
      isSuccess
        ? XApiEventKeys.classCodeModalSuccess
        : XApiEventKeys.classCodeModalFailure,
      {
        result: {
          source,
          shouldJoinClass,
          isSuccess,
        },
      },
      {
        result: {
          isRightClick: false,
          classCode,
          ...commonXapiContextPayload,
        },
      },
    );
  };

  const handleRedirect = (codeInfo: JoinCodeInfo | null) => {
    // check codeInfo data to find out code validation result
    if (!codeInfo || !codeInfo.source || !codeInfo.code) {
      sendEnterCodeXAPIEvent({
        isSuccess: false,
        source: null,
        shouldJoinClass: false,
        classCode: codeInfo?.code,
      });

      return;
    }

    const { source, code, shouldJoinClass } = codeInfo;
    const isSingleSessionCode = source === CodeTypes.singleSession;

    // keep showing the modal when amplify-enrollment code is entered and request to join class is pending
    if (!isSingleSessionCode && !shouldJoinClass) {
      return;
    }

    // truthy shouldJoinClass indicates that student successfully joined class
    if (!isSingleSessionCode && shouldJoinClass) {
      handleJoinComponentClose();

      refetchUserClassesWithCurrentArgs();
      refetchAssignmentsWithCurrentArgs();
      refetchUserAssetsWithCurrentArgs();

      const event = new CustomEvent('successfulClassCodeJoin');
      window.dispatchEvent(event);

      sendEnterCodeXAPIEvent({
        isSuccess: true,
        classCode: code,
        shouldJoinClass,
        source,
      });

      setTimeout(() => {
        notificationsService.showNotification(JOIN_CODE_NOTIFICATION_MESSAGE);
      }, NOTIFICATION_SHOW_DELAY);

      return;
    }

    sendEnterCodeXAPIEvent({
      isSuccess: true,
      classCode: code,
      shouldJoinClass: false,
      source,
    });

    // Redirect to a Desmos activity using the code.
    window.location.href = `${
      getAmpConfig().desmosStudent
    }/join/${code}?checkAmplifyLogin=true`;
  };

  return (
    <>
      {isSingleSessionCodeModalOpen && (
        <Join
          keycloakClient={keycloak}
          appElementId="#join-code-modal"
          onClose={handleJoinComponentClose}
          isOnJoin={false}
          isOpen={isSingleSessionCodeModalOpen}
          closeButtonClassName={'close-button session-code-modal-close-btn'}
          externalClassName={clsx('session-code-modal', {
            'session-code-modal__elementary': !isMiddleSchoolView,
          })}
          setCodeInfo={handleRedirect}
        />
      )}
      <div id="join-code-modal" />
    </>
  );
};
